import { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Réduit la photo côté tablette (max 900 px, JPEG) avant l'envoi : rapide et léger.
async function shrink(file) {
  try {
    const bmp = await createImageBitmap(file);
    const k = Math.min(1, 900 / Math.max(bmp.width, bmp.height));
    const c = document.createElement("canvas");
    c.width = Math.round(bmp.width * k); c.height = Math.round(bmp.height * k);
    c.getContext("2d").drawImage(bmp, 0, 0, c.width, c.height);
    const blob = await new Promise((r) => c.toBlob(r, "image/jpeg", 0.85));
    return new File([blob], "photo.jpg", { type: "image/jpeg" });
  } catch { return file; }
}

// 🖼️ Choix de l'image d'un rêve : galerie prête + photo de la tablette.
export default function DreamImagePicker({ token, goal, onChosen, onClose }) {
  const [gallery, setGallery] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef();

  useEffect(() => {
    axios.get(`${API}/cataley/public/${token}/gallery`).then((r) => setGallery(r.data.images || [])).catch(() => setGallery([]));
  }, [token]);

  const pick = async (url) => {
    setBusy(true); setErr("");
    try { const r = await axios.put(`${API}/cataley/public/${token}/goals/${goal.id}/image`, { image_url: url }); onChosen(r.data.image_url); }
    catch { setErr("Oups, réessaie !"); } finally { setBusy(false); }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", await shrink(f));
      const r = await axios.post(`${API}/cataley/public/${token}/goals/${goal.id}/photo`, fd, { timeout: 60000 });
      onChosen(r.data.image_url);
    } catch (ex) { setErr(ex?.response?.data?.detail || "La photo n'a pas pu être envoyée"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-3 sm:items-center" data-testid="dream-image-picker" onClick={onClose}>
      <div className="w-full max-w-md rounded-3xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-black text-stone-800">Une image pour « {goal.title} » 🖼️</h3>
        <p className="text-sm text-stone-500">Touche l'image qui ressemble à ton rêve, ou prends une vraie photo.</p>
        <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-5">
          {gallery.map((g) => (
            <button key={g.id} onClick={() => pick(g.url)} disabled={busy} data-testid={`dream-gallery-${g.id}`}
              className={`overflow-hidden rounded-2xl border-4 transition-transform active:scale-95 ${goal.image_url === g.url ? "border-emerald-400" : "border-transparent hover:border-indigo-200"}`}>
              <img src={g.url} alt={g.label} className="aspect-square w-full object-cover" loading="lazy" />
              <span className="block truncate bg-stone-50 px-1 py-0.5 text-[10px] font-semibold text-stone-600">{g.label}</span>
            </button>
          ))}
        </div>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={upload} data-testid="dream-photo-input" />
        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="dream-photo-btn"
            className="flex-1 rounded-full bg-indigo-500 px-4 py-3 font-bold text-white shadow hover:bg-indigo-600 disabled:opacity-50">📷 {busy ? "Un instant…" : "Prendre / choisir une photo"}</button>
          {goal.image_url && <button onClick={() => pick("")} disabled={busy} data-testid="dream-image-clear" className="rounded-full border border-stone-200 px-4 py-3 text-sm font-semibold text-stone-600">Sans image</button>}
          <button onClick={onClose} data-testid="dream-image-close" className="rounded-full border border-stone-200 px-4 py-3 text-sm font-semibold text-stone-600">Fermer</button>
        </div>
        {err && <p className="mt-2 text-sm font-semibold text-rose-600" data-testid="dream-image-error">{err}</p>}
      </div>
    </div>
  );
}
